import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FramerButton, TextInfoGroup } from "@/components/shared";
import { CLRPageModals, CustomerPageModals } from "@/constants/enums";
import { CreditLimitRequest } from "@/graphql/__generated__/graphql-operations";
import { formatNumberLocale } from "@/utils/helpers";
import { Dispatch, JSX, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";

type Props = {
  showModal: CLRPageModals | CustomerPageModals;
  setShowModal: Dispatch<SetStateAction<CLRPageModals|CustomerPageModals>>;
  currentCLR: CreditLimitRequest;
}

/**
 * A modal component that displays the details of a Credit Limit Request
 * @param {Props} props
 * @returns {JSX.Element} CLRDetailsModal component
 */
function CLRDetailsModal(props: Props): JSX.Element {

  // Props
  const { showModal, setShowModal, currentCLR } = props;

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["modals", "common"]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(CLRPageModals.NoModal as any);
    }
  };

  const isOpen = showModal === CLRPageModals.CLRDetails || showModal === CustomerPageModals.CustomerDetails;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="dark:text-white">{t("clrdetails.title")}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-4">
          <TextInfoGroup label={t("clrdetails.labels.serviceprovider")} content={currentCLR.serviceprovider.name} />
          <TextInfoGroup label={t("clrdetails.labels.customer")} content={currentCLR.customer.name} />
          <TextInfoGroup label={t("clrdetails.labels.amount")} content={formatNumberLocale(intl, currentCLR.requestedAmount, "currency")} />
          <TextInfoGroup label={t("clrdetails.labels.reasonrequest")} content={currentCLR.reasonForRequest ?? "Not available"} />
          <TextInfoGroup label={t("clrdetails.labels.reasondenial")} content={currentCLR.reasonForDenial ?? "Not available"} />
        </div>

        <DialogFooter>
          {/* Action Buttons */}
          <div className="mt-4">
            <FramerButton
              onClick={() => {
                setShowModal(CLRPageModals.NoModal as any);
              }}
              variant="outline"
              className="border-mforange text-mforange bg-transparent dark:border-mforange"
            >
              {t("common:buttons.close")}
            </FramerButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CLRDetailsModal;
